// src/component/Cart.jsx
import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";

/** ===== Магазины и пикер (вертикальный список с обводкой) ===== */

const STORES = [
  { id: "ivanovskaya", label: "ул. Ивановская 6/4" },
  { id: "stolitya43", label: "ул. Проспект Столетия Владивостоку 43" },
  { id: "more", label: "ТЦ Море" },
];
const DEFAULT_STORE = STORES[0].label;

// Иконки (inline SVG)
const MapPin = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" />
  </svg>
);
const Check = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path d="M20.285 6.709a1 1 0 0 0-1.414-1.414L9 15.167 5.121 11.29a1 1 0 1 0-1.414 1.414l4.95 4.95a1 1 0 0 0 1.415 0l10.213-10.945Z" />
  </svg>
);

// Вертикальный список в общем контейнере с рамкой
function StorePicker({ value, onChange, options }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-2">
      <ul className="list-none m-0 p-0 space-y-2">
        {options.map((opt) => {
          const active = value === opt.label;
          return (
            <li key={opt.id}>
              <label
                className={`relative flex items-start gap-3 px-4 py-3 pr-12
                            rounded-lg border transition
                            ${
                              active
                                ? "border-orange-400 bg-orange-50 ring-2 ring-orange-200"
                                : "border-gray-200 hover:border-gray-300"
                            }
                            focus-within:ring-2 focus-within:ring-orange-200`}
              >
                <input
                  type="radio"
                  name="store_location"
                  value={opt.label}
                  checked={active}
                  onChange={() => onChange(opt.label)}
                  className="sr-only"
                />
                <span className="inline-flex shrink-0 items-center justify-center h-7 w-7 rounded-md bg-orange-100 mt-0.5">
                  <MapPin className="h-4 w-4 fill-orange-600" />
                </span>
                <span className="text-sm font-medium leading-snug whitespace-normal break-words">
                  {opt.label}
                </span>
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Check
                    className={`h-5 w-5 transition-opacity ${
                      active ? "fill-orange-600 opacity-100" : "opacity-0"
                    }`}
                  />
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** ===== Утилиты ===== */

// формат ₽
const fmt = (n) =>
  Number(n || 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

// вычислить min price и total qty по данным товара
function pickStatsFromProduct(p) {
  const stores = Array.isArray(p?.stores) ? p.stores : [];
  const prices = stores
    .filter((s) => s.price != null)
    .map((s) => Number(s.price));
  const minPrice = prices.length ? Math.min(...prices) : null;
  const totalQty = stores.reduce(
    (acc, s) => acc + (Number(s.quantity) || 0),
    0
  );
  return { minPrice, totalQty };
}

/* мини-SVG «машинка» для кнопки */
const Truck = ({ className, style }) => (
  <svg
    viewBox="0 0 140 60"
    className={className}
    style={style}
    aria-hidden="true"
  >
    <rect x="5" y="18" width="80" height="28" rx="6" fill="#ff7a1a" />
    <rect x="85" y="24" width="30" height="22" rx="6" fill="#ff9a42" />
    <rect x="92" y="28" width="13" height="10" rx="2" fill="white" />
    <rect x="5" y="14" width="80" height="6" rx="3" fill="#ffa45a" />
    <circle cx="25" cy="50" r="8" className="tw tw1" fill="#333" />
    <circle cx="25" cy="50" r="4" fill="#bbb" />
    <circle cx="70" cy="50" r="8" className="tw tw2" fill="#333" />
    <circle cx="70" cy="50" r="4" fill="#bbb" />
    <circle cx="98" cy="50" r="8" className="tw tw3" fill="#333" />
    <circle cx="98" cy="50" r="4" fill="#bbb" />
  </svg>
);

/** ===== Основной компонент ===== */

const Cart = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    patronymic: "",
    phone: "",
    address: "",
    email: "",
  });
  const [submitted, setSubmitted] = useState(false);

  // по умолчанию — самовывоз + выбранный магазин
  const [deliveryMethod, setDeliveryMethod] = useState("pickup");
  const [storeLocation, setStoreLocation] = useState(DEFAULT_STORE);

  const [authError, setAuthError] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchItems();
    fetchProfile();
  }, []);

  // дефолт магазина при переключении на самовывоз
  useEffect(() => {
    if (deliveryMethod === "pickup" && !storeLocation) {
      setStoreLocation(DEFAULT_STORE);
    }
  }, [deliveryMethod, storeLocation]);

  async function fetchProfile() {
    try {
      const { data, status } = await authFetch("/api/accounts/profile/");
      if (status === 401) {
        setAuthError(true);
        return;
      }
      setForm((prev) => ({
        ...prev,
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        patronymic: data.patronymic || "",
        phone: data.phone || "",
        email: data.email || "",
      }));
    } catch {}
  }

  // обогащаем позиции minPrice/totalQty, если не пришли с сервера
  const enrichItems = async (raw) => {
    const list = Array.isArray(raw) ? raw : [];
    const needFetchIds = list
      .filter(
        (it) => it.product_min_price == null || it.product_total_qty == null
      )
      .map((it) => it.product ?? it.product_id);

    const fetchedMap = {};
    await Promise.all(
      [...new Set(needFetchIds)].map(async (id) => {
        try {
          const res = await fetch(`/api/catalog/product/${id}/`);
          if (res.ok) {
            const product = await res.json();
            fetchedMap[id] = pickStatsFromProduct(product);
          }
        } catch {}
      })
    );

    return list.map((it) => {
      const pid = it.product ?? it.product_id;
      const fb = fetchedMap[pid] || {};
      return {
        ...it,
        product_min_price:
          it.product_min_price ?? fb.minPrice ?? it.product_price ?? 0,
        product_total_qty: it.product_total_qty ?? fb.totalQty ?? it.stock ?? 0,
      };
    });
  };

  const fetchItems = async () => {
    try {
      const { data, status } = await authFetch("/api/orders/cart/");
      if (status === 401) {
        setAuthError(true);
        return;
      }
      const withStats = await enrichItems(data);
      setItems(withStats);
    } catch (e) {
      console.error("Ошибка загрузки корзины:", e);
      setAuthError(true);
    }
  };

  const handleInputChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const formatPhone = (phone) => phone.replace(/\D/g, "");

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validateEmail(form.email)) {
      alert("Пожалуйста, введите корректный E-mail!");
      return;
    }

    try {
      setSubmitting(true);
      const { status } = await authFetch("/api/orders/order/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          phone: formatPhone(form.phone),
          delivery_method: deliveryMethod,
          store_location: storeLocation,
          comment,
        }),
      });
      if (status === 401) {
        setAuthError(true);
        setSubmitting(false);
        return;
      }
      setItems([]);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert("Ошибка оформления заказа");
    } finally {
      setSubmitting(false);
    }
  };

  // Итого по корзине
  const total = items.reduce((acc, it) => {
    const price = Number(it.product_min_price ?? it.product_price) || 0;
    const qty = Number(it.quantity) || 0;
    return acc + price * qty;
  }, 0);
  // Доставка Владивосток — 300 ₽
  const DELIVERY_FEE_VVO = 300;
  const deliveryFee = deliveryMethod === "delivery" ? DELIVERY_FEE_VVO : 0;
  const totalWithDelivery = total + deliveryFee;

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      const { data, status } = await authFetch("/api/orders/cart/update/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: itemId, quantity: newQuantity }),
      });

      if (status === 401) {
        setAuthError(true);
        return;
      }
      if (status === 409) {
        alert((data && data.error) || "Недостаточно товара на складе.");
        await fetchItems();
        return;
      }

      const withStats = await enrichItems(data);
      setItems(withStats);
    } catch (e) {
      console.error(e);
      alert("Не удалось обновить количество");
    }
  };

  const removeItem = async (itemId) => {
    try {
      const { data, status } = await authFetch("/api/orders/cart/delete/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: itemId }),
      });
      if (status === 401) {
        setAuthError(true);
        return;
      }
      const withStats = await enrichItems(data);
      setItems(withStats);
    } catch (e) {
      console.error(e);
      alert("Ошибка удаления");
    }
  };
  // Полная очистка корзины
  const clearCart = async () => {
    try {
      const { status } = await authFetch("/api/orders/cart/clear/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (status === 401) {
        setAuthError(true);
        return;
      }
      await fetchItems();
    } catch (e) {
      // fallback — удаляем все позиции по одной
      try {
        await Promise.all(
          items.map((it) =>
            authFetch("/api/orders/cart/delete/", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ item_id: it.id }),
            }).catch(() => {})
          )
        );
      } finally {
        await fetchItems();
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white shadow-2xl rounded-2xl">
      <style>{`
    @keyframes truckRunBtn { 0% { transform: translateX(-110%);} 100% { transform: translateX(110%);} }
    @keyframes roadMove { 0% { background-position-x: 0;} 100% { background-position-x: -56px;} }
    @keyframes wheelSpin { to { transform: rotate(360deg);} }
    .tw1 { transform-origin: 25px 50px; animation: wheelSpin 0.5s linear infinite; }
    .tw2 { transform-origin: 70px 50px; animation: wheelSpin 0.5s linear infinite; }
    .tw3 { transform-origin: 98px 50px; animation: wheelSpin 0.5s linear infinite; }
  `}</style>

<div className="mb-6">
  <h1 className="flex items-center gap-3 text-3xl font-extrabold text-orange-700">
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-orange-100">🛒</span>
    Ваша корзина
  </h1>

  {/* большая оранжевая полоса */}
  <div className="mt-2 h-[6px] w-full rounded-full bg-orange-500"></div>
</div>


      {authError && (
        <div className="text-red-500 text-center text-lg font-semibold my-6">
          Для оформления заказа необходимо авторизоваться.
        </div>
      )}

      {items.length === 0 && !submitted && (
        <p className="text-gray-500 text-center">
          Ваша корзина пуста. Добавьте товары, чтобы оформить заказ.
        </p>
      )}

      {submitted && (
        <p className="text-green-600 font-bold text-xl text-center">
          Спасибо за заказ! Мы свяжемся с вами.
        </p>
      )}

      {items.length > 0 && !submitted && (
        <>
          {/* Список + колонка заказа */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Список позиций */}
            <div className="lg:col-span-2">
              {/* Очистить корзину */}
              <div className="mb-2 text-right">
                <button
                  type="button"
                  onClick={clearCart}
                  className="font-bold uppercase tracking-wide text-orange-500 hover:underline"
                >
                  очистить
                </button>
              </div>
              <ul className="space-y-4 mb-8">
                {items.map((item) => {
                  const stock = Number(
                    item.product_total_qty ?? item.stock ?? 0
                  );
                  const price =
                    Number(item.product_min_price ?? item.product_price) || 0;
                  const qty = Number(item.quantity) || 0;
                  const lineTotal = price * qty;

                  return (
                    <li
                      key={item.id}
                      className="p-4 bg-gray-50 rounded-xl shadow-sm flex flex-col sm:flex-row sm:justify-between items-center border border-gray-100"
                    >
                      <div className="flex flex-col gap-1 text-center sm:text-left">
                        <h3 className="font-semibold text-lg tracking-tight">
                          {item.product_name}
                        </h3>
                        <span className="text-sm text-gray-500">
                          Цена: {fmt(price)} ₽ / Остаток: {fmt(stock)}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 mt-3 sm:mt-0">
                        <button
                          onClick={() => updateQuantity(item.id, qty - 1)}
                          className="px-3 py-1 bg-orange-100 hover:bg-orange-200 rounded-lg transition"
                        >
                          −
                        </button>

                        <span className="w-10 text-center font-semibold">
                          {fmt(qty)}
                        </span>

                        <button
                          onClick={() => updateQuantity(item.id, qty + 1)}
                          disabled={qty >= stock}
                          className="px-3 py-1 bg-orange-100 hover:bg-orange-200 rounded-lg disabled:opacity-50 transition"
                        >
                          +
                        </button>

                        <span className="ml-4 font-bold text-orange-600 whitespace-nowrap">
                          {fmt(lineTotal)} ₽
                        </span>

                        <button
                          onClick={() => removeItem(item.id)}
                          className="ml-4 text-red-600 hover:underline"
                        >
                          ✕
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Колонка с итогом и формой */}
            <div className="lg:col-span-1">
              <div className="sticky top-6 space-y-4">
                {/* Форма заказа */}
                <form
                  onSubmit={handleOrderSubmit}
                  className="bg-gray-50 p-6 rounded-xl shadow-md space-y-4 border border-gray-100"
                >
                  <h3 className="text-xs font-bold text-orange-600 tracking-wider uppercase mb-2">
                    Контакты получателя
                  </h3>
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Фамилия"
                    required
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                    value={form.last_name}
                    onChange={handleInputChange}
                  />

                  <input
                    type="text"
                    name="first_name"
                    placeholder="Имя"
                    required
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                    value={form.first_name}
                    onChange={handleInputChange}
                  />

                  <input
                    type="text"
                    name="patronymic"
                    placeholder="Отчество (если есть)"
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                    value={form.patronymic}
                    onChange={handleInputChange}
                  />

                  <input
                    type="tel"
                    name="phone"
                    placeholder="Телефон"
                    required
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                    value={form.phone}
                    onChange={handleInputChange}
                  />

                  <input
                    type="email"
                    name="email"
                    placeholder="E-mail"
                    required
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                    value={form.email}
                    onChange={handleInputChange}
                  />
                  {/* Тумблер получение */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Способ получения
                    </label>
                    <div
                      className="relative grid grid-cols-2 gap-0 rounded-lg bg-gray-100 p-1 text-sm font-medium"
                      role="tablist"
                      aria-label="Способ получения"
                    >
                      <span
                        className={`absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-md bg-white shadow transition-transform ${
                          deliveryMethod === "delivery"
                            ? "translate-x-full"
                            : ""
                        }`}
                        aria-hidden="true"
                      />
                      <button
                        type="button"
                        role="tab"
                        aria-selected={deliveryMethod === "pickup"}
                        onClick={() => setDeliveryMethod("pickup")}
                        className={`z-10 py-2 rounded-md transition ${
                          deliveryMethod === "pickup"
                            ? "text-orange-600"
                            : "text-gray-600"
                        }`}
                      >
                        Самовывоз
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={deliveryMethod === "delivery"}
                        onClick={() => setDeliveryMethod("delivery")}
                        className={`z-10 py-2 rounded-md transition ${
                          deliveryMethod === "delivery"
                            ? "text-orange-600"
                            : "text-gray-600"
                        }`}
                      >
                        Доставка
                      </button>
                    </div>
                  </div>
                  {deliveryMethod === "pickup" ? (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Магазин для самовывоза
                      </label>

                      {/* Контейнер со сплошной обводкой + карточки с рамкой */}
                      <StorePicker
                        value={storeLocation}
                        onChange={setStoreLocation}
                        options={STORES}
                      />

                      {/* Скрытое поле, чтобы HTML5 required продолжал работать */}
                      <input
                        type="text"
                        tabIndex={-1}
                        aria-hidden="true"
                        className="hidden"
                        required
                        value={storeLocation || ""}
                        onChange={() => {}}
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Адрес доставки
                      </label>
                      <p className="text-xs text-gray-500">
                        Стоимость доставки — {fmt(DELIVERY_FEE_VVO)} ₽ в
                        пределах города Владивосток.
                      </p>
                      <textarea
                        name="address"
                        placeholder="Город, улица, дом, квартира"
                        required
                        className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                        value={form.address}
                        onChange={handleInputChange}
                        rows={3}
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <label className="text-gray-700 text-sm font-bold">
                      Комментарий к заказу
                    </label>
                    <textarea
                      className="form-textarea w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-300"
                      rows={3}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Любые пожелания по заказу..."
                    />
                  </div>
                  {/* Итог заказа — внизу формы */}
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between text-base font-bold">
                      <span className="text-gray-700">Итого:</span>
                      <span className="text-orange-600">
                        {fmt(totalWithDelivery)} ₽
                      </span>
                    </div>
                    {deliveryMethod === "delivery" && (
                      <div className="mt-1 text-xs text-gray-500">
                        Включая доставку {fmt(DELIVERY_FEE_VVO)} ₽ в пределах
                        города Владивосток.
                      </div>
                    )}
                  </div>
                  {/* Кнопка с анимацией */}
                  <button
                    type="submit"
                    disabled={submitting}
                    aria-busy={submitting}
                    className={`relative overflow-hidden w-full py-3 rounded-md text-lg transition-all
                      ${
                        submitting
                          ? "bg-[#FFE6D3] text-orange-900 cursor-wait"
                          : "bg-orange-500 text-white hover:bg-orange-600"
                      }`}
                  >
                    {submitting && (
                      <span
                        className="absolute inset-0 bg-[#FFE6D3]"
                        aria-hidden="true"
                      />
                    )}
                    <span className="relative z-[1] flex items-center justify-center gap-3 font-semibold">
                      {submitting ? "Оформляем…" : "Оформить заказ"}
                      {submitting && (
                        <span className="relative h-7 w-28 overflow-hidden">
                          <span
                            className="absolute left-0 right-0 bottom-[2px] h-[3px]"
                            style={{
                              backgroundImage:
                                "repeating-linear-gradient(90deg,#ffcf9f 0 14px, transparent 14px 28px)",
                              backgroundSize: "28px 3px",
                              animation: "roadMove 0.6s linear infinite",
                            }}
                            aria-hidden="true"
                          />
                          <Truck
                            className="absolute left-0 top-[-6px] h-8 w-[80px]"
                            style={{
                              animation: "truckRunBtn 1.4s linear infinite",
                            }}
                          />
                        </span>
                      )}
                    </span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
